import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { SvgImage } from 'components/images/SvgImage';
import { DatePickerInput } from 'components/inputs/DatePickerInput';
import { TextInput } from 'components/inputs/TextInput';
import { useGetOrganisation } from 'features/organisations/hooks/useGetOrganisation';
import { useGetOrganisations } from 'features/organisations/hooks/useGetOrganisations';
import { IconToggleButtonGroup, LazyAutocompleteInput, Logo } from 'greener-ui-library';
import useAuth from 'hooks/useAuth';
import { Organisation } from 'types';
import { DATE_FORMAT } from 'utils/constants';
import { formInputStyles } from '../styles';
import { BlueprintFormData } from '../types';
import { applicationTypes } from '../utils/applicationTypes';
import { HybridReason, hybridReasonTranslationKeys } from '../utils/constants';
import { BlueprintStepSection } from './BlueprintStepSection';
import { ResponsiveFormContainer } from './ResponsiveFormContainer';

interface AboutProjectStepProps {
  formMethods: UseFormReturn<BlueprintFormData>;
  blueprintId?: number;
  mode?: 'create' | 'edit';
}

export const AboutProjectStep = ({ formMethods, mode }: AboutProjectStepProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [organisationSearchTerm, setOrganisationSearchTerm] = useState('');
  const [hasEditedPartnerOrg, setHasEditedPartnerOrg] = useState(false);
  const { control, trigger, setValue, watch } = formMethods;

  const hybridReasonOptions = Object.values(HybridReason);
  const selectedReasons = watch('hybridReasons') ?? [];
  const selectedOrganisation = watch('partnerOrganisation') ?? null;

  const userOrganisationId = user?.organisation?.organisationId?.toString() ?? '';
  const { organisation: userOrganisation } = useGetOrganisation(userOrganisationId);

  const {
    organisations,
    isFetching: isOrganisationsFetching,
    goToNextPage,
    hasNextPage,
  } = useGetOrganisations({
    accumulateData: true,
    searchTerm: organisationSearchTerm,
    reactQueryConfig: { suspense: false },
  });

  const accumulatedOrgs = (organisations as { accumulatedData?: Organisation[] })?.accumulatedData ?? [];
  const logoUrl = selectedOrganisation?.logoUrl;

  const shouldPrefillPartnerOrg =
    mode === 'create' && userOrganisation?.organisationId && !selectedOrganisation && !hasEditedPartnerOrg;

  useEffect(() => {
    if (shouldPrefillPartnerOrg) {
      setValue('partnerOrganisation', userOrganisation, { shouldDirty: false });
      setValue('partnerOrganisationId', userOrganisation.organisationId, { shouldDirty: false });
    }
  }, [mode, userOrganisation, shouldPrefillPartnerOrg, setValue]);

  const handleReasonChange = (value: string) => {
    const updatedReasons = selectedReasons.includes(value)
      ? selectedReasons.filter((reason) => reason !== value)
      : [...selectedReasons, value];

    setValue('hybridReasons', updatedReasons, { shouldDirty: true });
  };

  return (
    <>
      <BlueprintStepSection title={t('blueprints:wizard.sections.generalInfo')}>
        <ResponsiveFormContainer sx={{ mb: 2 }}>
          <TextInput
            control={control}
            name="blueprintName"
            label={`${t('blueprints:wizard.fields.blueprintName')} *`}
            placeholder={t('blueprints:wizard.fields.blueprintName')}
            sx={formInputStyles}
          />
          <TextInput
            control={control}
            name="clientName"
            label={`${t('blueprints:wizard.fields.clientName')} *`}
            placeholder={t('blueprints:wizard.fields.clientName')}
            sx={formInputStyles}
          />
        </ResponsiveFormContainer>

        <ResponsiveFormContainer>
          <LazyAutocompleteInput<Organisation, BlueprintFormData>
            data-testid="partner-organisation-autocomplete"
            control={control}
            name="partnerOrganisation"
            label={`${t('blueprints:wizard.fields.partnerOrganisation')} *`}
            placeholder={t('entities:organisation.selectOrganisation')}
            options={accumulatedOrgs}
            loading={isOrganisationsFetching}
            onSearchChange={setOrganisationSearchTerm}
            onLoadMore={goToNextPage}
            hasNextPage={hasNextPage}
            getOptionLabel={(item) => item?.name ?? ''}
            isOptionEqualToValue={(option, value) => option.organisationId === value.organisationId}
            onChange={(_, organisation) => {
              setHasEditedPartnerOrg(true);
              const newOrg = organisation ?? null;
              setValue('partnerOrganisationId', newOrg?.organisationId ?? null, { shouldDirty: true });
            }}
            gridSize={12}
          />
          <Box>
            <Logo
              logoUrl={logoUrl}
              height={40}
              width={logoUrl ? 'auto' : 60}
              representativeChar={selectedOrganisation?.name?.charAt(0).toUpperCase() ?? '-'}
              svgImage={logoUrl ? <SvgImage logoUrl={logoUrl} /> : undefined}
              imagePosition="left center"
              sx={{ justifyContent: logoUrl ? 'flex-start' : 'center' }}
            />
          </Box>
        </ResponsiveFormContainer>
      </BlueprintStepSection>
      <BlueprintStepSection title={t('blueprints:wizard.fields.applicationType.title')} titleSpacing={24}>
        <IconToggleButtonGroup
          options={applicationTypes.map((type) => ({ ...type, label: t(type.label) }))}
          value={formMethods.watch('applicationType') || ''}
          onChange={(value) => formMethods.setValue('applicationType', value || '')}
        />
      </BlueprintStepSection>

      <BlueprintStepSection title={t('blueprints:wizard.fields.projectDuration.title')} titleSpacing={8}>
        <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.primary' }}>
          {t('blueprints:wizard.fields.projectDuration.helperText')}
        </Typography>
        <ResponsiveFormContainer maxColumnWidth={190} minColumnWidth={190}>
          <DatePickerInput
            control={control}
            name="startDate"
            label={`${t('blueprints:wizard.fields.projectDuration.startDate')} *`}
            showTimeInput={false}
            gridSize={false}
            fullWidth={true}
            size="small"
            dateFormat={DATE_FORMAT}
            initialHours={0}
            initialMinutes={0}
            initialSeconds={0}
            onChange={() => trigger('endDate')}
          />
          <DatePickerInput
            control={control}
            name="endDate"
            label={t('blueprints:wizard.fields.projectDuration.endDate')}
            showTimeInput={false}
            gridSize={false}
            fullWidth={true}
            size="small"
            dateFormat={DATE_FORMAT}
            initialHours={23}
            initialMinutes={59}
            initialSeconds={59}
            placeholder={t('blueprints:wizard.fields.projectDuration.endDatePlaceholder')}
            isClearable
            onChange={({ date }) => {
              if (date === null) {
                setValue('endDate', null as unknown as Date, { shouldValidate: false, shouldDirty: true });
              }
              trigger('endDate');
            }}
          />
        </ResponsiveFormContainer>
      </BlueprintStepSection>

      <BlueprintStepSection title={t('blueprints:wizard.fields.hybridReasons.title')} titleSpacing={8}>
        <FormGroup sx={{ mb: 2 }}>
          {hybridReasonOptions.map((reason) => (
            <FormControlLabel
              key={reason}
              control={
                <Checkbox checked={selectedReasons.includes(reason)} onChange={() => handleReasonChange(reason)} />
              }
              label={t(`blueprints:wizard.fields.hybridReasons.options.${hybridReasonTranslationKeys[reason]}`)}
              sx={{ width: 'fit-content' }}
            />
          ))}
        </FormGroup>

        <ResponsiveFormContainer minColumnWidth={400} maxColumnWidth={800}>
          <TextInput
            control={control}
            name="hybridReasonsCustom"
            label={t('blueprints:wizard.fields.hybridReasons.customReason')}
            placeholder={t('blueprints:wizard.fields.hybridReasons.customReason')}
            sx={formInputStyles}
          />
        </ResponsiveFormContainer>
      </BlueprintStepSection>
    </>
  );
};
